import imageCompression from "browser-image-compression";

/**
 * Converts and compresses an image file to WebP format with high quality.
 * Falls back to JPEG if WebP canvas export isn't supported.
 */
export async function optimizeImage(
  file: File,
  options?: { maxWidthOrHeight?: number; maxSizeMB?: number }
): Promise<File> {
  // Preserve quality: keep up to 4K resolution and a generous size budget.
  const maxWidthOrHeight = options?.maxWidthOrHeight ?? 3840;
  const maxSizeMB = options?.maxSizeMB ?? 8;

  // Skip if already a small WebP
  if (file.type === "image/webp" && file.size < maxSizeMB * 1024 * 1024) {
    return file;
  }

  // First pass: compress + resize
  const compressed = await imageCompression(file, {
    maxSizeMB,
    maxWidthOrHeight,
    useWebWorker: true,
    fileType: "image/webp",
    initialQuality: 0.95,
  });

  // Ensure .webp extension
  const baseName = file.name.replace(/\.[^.]+$/, "");
  const webpFile = new File([compressed], `${baseName}.webp`, {
    type: "image/webp",
    lastModified: Date.now(),
  });

  return webpFile;
}

/**
 * Optimizes avatar images — smaller dimensions, tighter compression.
 */
export async function optimizeAvatar(file: File): Promise<File> {
  return optimizeImage(file, { maxWidthOrHeight: 512, maxSizeMB: 0.5 });
}

/**
 * Converts video to a web-optimized format using MediaRecorder + canvas re-encode.
 * Produces a smaller WebM or MP4 depending on browser support.
 * For very large videos, trims to maxDurationSec.
 */
export async function optimizeVideo(
  file: File,
  options?: { maxDurationSec?: number; targetHeight?: number }
): Promise<File> {
  const maxDuration = options?.maxDurationSec ?? 120;
  const targetHeight = options?.targetHeight ?? 720;

  // If already small enough, skip
  if (file.size < 5 * 1024 * 1024) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";

    const url = URL.createObjectURL(file);
    video.src = url;

    video.onloadedmetadata = () => {
      // Calculate dimensions maintaining aspect ratio
      const aspect = video.videoWidth / video.videoHeight;
      const height = Math.min(video.videoHeight, targetHeight);
      const width = Math.round(height * aspect);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;

      // Determine best supported MIME type
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : "video/mp4";

      const ext = mimeType.startsWith("video/webm") ? "webm" : "mp4";

      const stream = canvas.captureStream(30);
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 1_500_000,
      });

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        URL.revokeObjectURL(url);
        const blob = new Blob(chunks, { type: mimeType });
        const baseName = file.name.replace(/\.[^.]+$/, "");
        const optimized = new File([blob], `${baseName}.${ext}`, {
          type: mimeType,
          lastModified: Date.now(),
        });
        resolve(optimized);
      };

      recorder.onerror = () => {
        URL.revokeObjectURL(url);
        // Fallback: return original if re-encode fails
        resolve(file);
      };

      const duration = Math.min(video.duration, maxDuration);
      recorder.start();

      const drawFrame = () => {
        if (video.paused || video.ended || video.currentTime >= duration) {
          recorder.stop();
          return;
        }
        ctx.drawImage(video, 0, 0, width, height);
        requestAnimationFrame(drawFrame);
      };

      video.currentTime = 0;
      video.play().then(drawFrame).catch(() => resolve(file));

      // Safety timeout
      setTimeout(() => {
        if (recorder.state === "recording") {
          video.pause();
          recorder.stop();
        }
      }, (duration + 2) * 1000);
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fallback to original
    };
  });
}

/**
 * Helper: get optimized file extension
 */
export function getOptimizedExt(file: File): string {
  if (file.type.startsWith("image/")) return "webp";
  if (file.type.includes("webm")) return "webm";
  if (file.type.includes("mp4")) return "mp4";
  return file.name.split(".").pop() || "bin";
}
